import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import store from "../../redux/store";
import { addAnswer } from "../../redux/survey/actions";
import { restoreTrainingProgress } from "../../redux/learning/actions";
import {
  selectLearningPackageData,
  selectLearningPackageId,
  selectLearningPackageLoading,
  selectLearningUserId,
} from "../../redux/learning/selectors";
import { mapLearningPackage } from "../helpers/mapLearningPackage";
import { LEARNING_PHASES } from "../constants/learningPhases";
import {
  TRAINING_PROGRESS_SAVE_MS,
  buildTrainingSnapshot,
  readTrainingProgress,
  removeTrainingProgress,
  shouldRestoreTrainingProgress,
  trainingProgressKey,
  writeTrainingProgress,
} from "../helpers/trainingProgressStorage";

function progressKey(state) {
  return trainingProgressKey({
    packageId: state.learning.packageId,
    userId: state.learning.userId,
    inviteId: state.learning.inviteId,
  });
}

function saveTrainingProgress() {
  const state = store.getState();
  const key = progressKey(state);
  if (!key || !state.learning.packageData) {
    return;
  }

  if (state.learning.phase === LEARNING_PHASES.SUCCESS) {
    removeTrainingProgress(key);
    return;
  }

  writeTrainingProgress(
    key,
    buildTrainingSnapshot(state.learning, state.survey?.answeres),
  );
}

function restoreSavedProgress(dispatch, packageData) {
  const state = store.getState();
  if (!shouldRestoreTrainingProgress(state.learning.phase)) {
    return;
  }

  const course = mapLearningPackage(packageData);
  const saved = readTrainingProgress(progressKey(state), course?.guidanceSteps?.length || 0);
  if (!saved) {
    return;
  }

  const step = course?.guidanceSteps?.[saved.guidanceIndex];
  dispatch(restoreTrainingProgress({
    phase: saved.phase,
    guidanceIndex: saved.guidanceIndex,
    questionIndex: saved.questionIndex,
    quizResult: saved.quizResult,
    document: step?.type === "document"
      ? {
        id: step.id,
        readPercent: saved.documentReadPercent,
        complete: saved.documentComplete,
        needsScroll: saved.documentNeedsScroll,
      }
      : null,
  }));

  Object.entries(saved.answers).forEach(([id, answer]) => {
    dispatch(addAnswer({
      id,
      value: answer.value,
      type: answer.type,
      score_exclude: answer.score_exclude,
    }));
  });
}

/** Saves where the learner is and restores it when the same training link opens again. */
export function useTrainingProgress() {
  const dispatch = useDispatch();
  const packageLoading = useSelector(selectLearningPackageLoading);
  const packageData = useSelector(selectLearningPackageData);
  const packageId = useSelector(selectLearningPackageId);
  const userId = useSelector(selectLearningUserId);
  const inviteId = useSelector((state) => state.learning.inviteId);
  const progressKeyForSession = trainingProgressKey({ packageId, userId, inviteId });
  const restoredKeyRef = useRef(null);

  useEffect(() => {
    if (packageLoading || !packageData || !progressKeyForSession) {
      return undefined;
    }

    if (restoredKeyRef.current !== progressKeyForSession) {
      restoredKeyRef.current = progressKeyForSession;
      restoreSavedProgress(dispatch, packageData);
    }

    const timer = window.setInterval(saveTrainingProgress, TRAINING_PROGRESS_SAVE_MS);
    const flush = () => saveTrainingProgress();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        flush();
      }
    };

    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [dispatch, packageData, packageLoading, progressKeyForSession]);
}
